import { Link, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { cn } from '@atrvd/saas-ui'
import { useBreadcrumbOverrides } from './BreadcrumbContext'

/**
 * Breadcrumb item
 */
interface BreadcrumbItem {
  label: string
  href?: string
}

/**
 * Route to breadcrumb mapping
 */
const routeLabels: Record<string, string> = {
  // Main sections
  banking: 'Banking',
  // Keys here are URL SEGMENTS, not page names: `dashboard` is the segment of
  // `/banking/dashboard`, whose page was renamed to "Daily Banking" (DCP-341)
  // without moving its URL. Deliberately NOT touching `banking` above — that
  // one is the shared first crumb of every /banking/* page.
  dashboard: 'Daily Banking',
  settings: 'Settings',
  // Segment of `/settings/financial-accounts` (DCP-324 renamed both the page
  // and its URL). Without an entry here the trail prints the raw slug,
  // "financial-accounts", hyphen and all.
  'financial-accounts': 'Financial Accounts',
  // The manual accounts that have no institution name (no name to show).
  'manual-unnamed': 'Unknown institution',
  transactions: 'Transactions',
  facilities: 'Facilities',
  integrations: 'Integrations',
  admin: 'Admin',

  // Banking subsections
  'cross-checking': 'Cross-checking',
  'transactions-import': 'Transactions Import',
  'balance-import': 'Balance Import',
  'outstanding-checks': 'Outstanding Checks',
  'loc-availability': 'LOC Availability',
  'credit-card-availability': 'Credit Card Availability',

  // Projections
  projections: 'Projections',
  // Shorter than the sidebar's "Cash Position Summary" — the trail reads
  // "Projections > Cash Position" per DCP-293.
  'cash-position': 'Cash Position',
  input: 'Projection Input',
  'projections-import': 'Projections Import',

  // Settings subsections
  profile: 'Profile',
  organization: 'Organization',
  users: 'Team',
  categories: 'Categories',
  packs: 'Packs',
  sage: 'Sage Intacct',

  // Admin subsections
  organizations: 'Organizations',
  invitations: 'Invitations',
}

/**
 * URL segments that only namespace a dynamic id and have no page of their own.
 * They are left out of the trail, so `/settings/financial-accounts/connections/:id`
 * reads "Settings > Financial Accounts > <connection name>" (and likewise
 * `/manual/:institutionName` for a manual bank). Only when NOT last: the last
 * segment is the dynamic value itself, and a bank may well be named "Manual".
 */
const HIDDEN_SEGMENTS = new Set(['connections', 'manual'])

/**
 * Whether the segment at `index` is the facility id of a facility's Financial
 * Accounts route (`/settings/facilities/:facilityId/financial-accounts…`). It
 * is left out: the facility reads as ONE crumb, "{Facility} Financial
 * Accounts", which the `financial-accounts` crumb shows (see
 * `useFacilityScopeBreadcrumb`).
 */
function isFacilityScopeId(segments: readonly string[], index: number): boolean {
  return segments[index - 1] === 'facilities' && segments[index + 1] === 'financial-accounts'
}

/**
 * A path segment as the page saw it. `location.pathname` keeps the
 * percent-encoding while route params arrive decoded, and a page registers its
 * label under the param — so a manual bank's name (encoded into its URL) is
 * looked up, and shown while unresolved, decoded.
 */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    // Malformed percent-encoding: the raw segment is the best available label.
    return segment
  }
}

/**
 * Breadcrumbs component for navigation hierarchy display
 */
export function Breadcrumbs() {
  const location = useLocation()
  const pathSegments = location.pathname.split('/').filter(Boolean)

  // DCP-296: labels registered by the page that owns a dynamic segment's data
  // (e.g. a facility id → its name), keyed by segment value. Breadcrumbs stays
  // presentational — it never fetches domain data, just reads what a page
  // registered via useSetBreadcrumb.
  const overrides = useBreadcrumbOverrides()

  const breadcrumbs: BreadcrumbItem[] = pathSegments.flatMap((segment, index) => {
    const isLast = index === pathSegments.length - 1
    if (HIDDEN_SEGMENTS.has(segment) && !isLast) return []
    if (isFacilityScopeId(pathSegments, index)) return []

    const href = '/' + pathSegments.slice(0, index + 1).join('/')
    const decoded = decodeSegment(segment)

    // A label registered for this crumb's whole PATH wins: that is how a
    // facility's page names its static `financial-accounts` segment "{Facility}
    // Financial Accounts". A path key starts with "/" and spells a whole route,
    // so a segment-value key (a bank name) could only collide by spelling out
    // that exact path. Until it registers, the static label shows.
    const pathLabel = overrides[href]
    if (pathLabel) return [{ label: pathLabel, href: isLast ? undefined : href }]

    // A known section keeps its static label except as the LAST segment:
    // overrides are keyed by value, so a bank named "settings" registering its
    // name must not relabel the Settings crumb. (Every caller registers a
    // dynamic id or name, never a static section.) Otherwise prefer a
    // page-registered override (e.g. "Bay City" for a facility id), then the
    // static route label, then the decoded segment (so an unresolved dynamic
    // id still shows something rather than breaking).
    const label =
      routeLabels[segment] && !isLast
        ? routeLabels[segment]
        : (overrides[decoded] ?? routeLabels[segment] ?? decoded)

    // Only KNOWN section segments (present in routeLabels) map to a real route,
    // so only those are links. A dynamic id segment (a facility/org id — whether
    // resolved to a name via an override or shown raw) has no standalone page:
    // linking it would land the user on an empty screen, so it renders as plain
    // text (DCP-296). The last crumb is never a link (it's the current page).
    const isKnownSection = Boolean(routeLabels[segment])

    return [{ label, href: !isLast && isKnownSection ? href : undefined }]
  })

  if (breadcrumbs.length === 0) {
    return null
  }

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-muted-foreground">
      {breadcrumbs.map((item, index) => (
        <div key={index} className="flex items-center gap-1">
          {index > 0 && <ChevronRight className="h-4 w-4" />}
          {item.href ? (
            <Link
              to={item.href}
              className="hover:text-foreground transition-colors"
            >
              {item.label}
            </Link>
          ) : (
            <span className={cn('font-medium text-foreground')}>
              {item.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  )
}